import { useQueryClient } from '@tanstack/react-query';
import {
  deleteWorkflowItemDocument2,
  updateWorkflowItemDocument,
  reorderWorkflowItemsMutationDocument,
  deleteWorkflowItemConditionDocument,
  createWorkflowItemMutationDocument2,
  createWorkflowItemConditionDocument2,
  updateWorkflowItemConditionDocument2,
  createWorkflowConditionalItemMutationDocument2,
} from 'api/workflow-items';
import { useRequestBuilderContext } from 'components/Requests/components/RequestsBuilder/context/RequestsBuilderContext';
import { alertGqlError } from 'components/Requests/components/RequestsBuilder/utils/get-error-message';
import {
  deleteWorkflowItemFromCache,
  addWorkflowSectionToCache,
  updateWorkflowConditionInCache,
  addWorkflowItemFromUpdateToCache,
  addWorkflowItemToCache,
} from 'components/Requests/components/RequestsBuilder/utils/mutation-utils';
import { useMutationGraphQL } from './useGraphQL';

export function useDeleteWorkflowItem() {
  const queryClient = useQueryClient();
  const { workflowId, setSubmitting, setSubmitted, setErrored } =
    useRequestBuilderContext();
  return useMutationGraphQL(deleteWorkflowItemDocument2, {
    onMutate: () => {
      setSubmitting(true);
    },
    onSettled: (response, _, { workflowItemId }) => {
      // Remove from the cache
      deleteWorkflowItemFromCache(queryClient, { workflowItemId }, workflowId);

      setSubmitted();
    },
    onError: (errors, { workflowItemId }) => {
      alertGqlError(errors, 'Failed to delete question');

      setErrored(workflowItemId, 'Failed to delete question');
      setSubmitting(false);
    },
  });
}

export function useUpdateWorkflowItem() {
  const queryClient = useQueryClient();
  const { workflowId, setSubmitting, setSubmitted, setErrored } =
    useRequestBuilderContext();
  return useMutationGraphQL(updateWorkflowItemDocument, {
    onMutate: () => {
      setSubmitting(true);
    },
    onSuccess: (response) => {
      if (!response?.updateWorkflowItem?.workflowItem) {
        setSubmitting(false);
        return;
      }

      addWorkflowItemFromUpdateToCache(
        queryClient,
        response?.updateWorkflowItem?.workflowItem,
        workflowId,
        true,
      );
      setSubmitted();
    },
    onError: (errors, { workflowItemId, prompt, type }) => {
      alertGqlError(errors, 'Failed to update question');

      if (prompt) {
        setErrored(workflowItemId, 'Failed to update question');
      }
      if (type) {
        setErrored(`${workflowItemId}.type`, 'Failed to update question type');
      }

      setSubmitting(false);
    },
  });
}

export function useCreateWorkflowItem() {
  const { setSubmitting, setSubmitted, setErrored } =
    useRequestBuilderContext();
  const queryClient = useQueryClient();
  return useMutationGraphQL(createWorkflowItemMutationDocument2, {
    onMutate: () => {
      setSubmitting(true);
    },
    onSuccess: (response, { workflowId }) => {
      if (!response?.createWorkflowItem2?.workflowItem) {
        //shouldn't happen
        setErrored('NEW', 'Failed to add question');
        setSubmitting(false);
        return;
      }

      let sanatizedResponseItem = response?.createWorkflowItem2?.workflowItem;

      // a very silly hack to handle the empty prompt that comes back from the server if we send them blank
      if (
        sanatizedResponseItem.prompt === 'Empty Prompt' &&
        sanatizedResponseItem.prompt.trim() === ''
      ) {
        sanatizedResponseItem = { prompt: '', ...sanatizedResponseItem };
      }

      addWorkflowItemToCache(queryClient, sanatizedResponseItem, workflowId);
      setSubmitted();
    },
    onError: (errors) => {
      alertGqlError(errors, 'Failed to add question');

      setErrored('NEW', 'Failed to add question');
      setSubmitting(false);
    },
  });
}

export function useCreateWorkflowConditionalItem() {
  const { setSubmitting, setSubmitted, setErrored } =
    useRequestBuilderContext();
  return useMutationGraphQL(createWorkflowConditionalItemMutationDocument2, {
    onMutate: () => {
      setSubmitting(true);
    },
    onSuccess: (response) => {
      if (!response?.createWorkflowItem2?.workflowItem) {
        //shouldn't happen
        setErrored('NEW', 'Failed to add question');
        setSubmitting(false);
        return;
      }

      setSubmitted();
    },
    onError: (errors) => {
      alertGqlError(errors, 'Failed to add question');

      setErrored('NEW', 'Failed to add question');
      setSubmitting(false);
    },
  });
}

export function useCreateWorkflowItemCondition2() {
  // const queryClient = useQueryClient();
  const { setSubmitting, setSubmitted, setErrored } =
    useRequestBuilderContext();
  return useMutationGraphQL(createWorkflowItemConditionDocument2, {
    onMutate: () => {
      setSubmitting(true);
    },
    onSettled: (response) => {
      if (!response?.createWorkflowItemCondition2?.workflowItemCondition) {
        setSubmitting(false);
        return;
      }
      setSubmitted();
    },
    onError: (errors, { workflowItemId }) => {
      alertGqlError(errors, 'Failed to add condition');

      setErrored(workflowItemId, 'Failed to add condition');
      setSubmitting(false);
    },
  });
}

export function useReorderWorkflowItems() {
  const queryClient = useQueryClient();
  const { workflowId, setSubmitting, setSubmitted } =
    useRequestBuilderContext();
  return useMutationGraphQL(reorderWorkflowItemsMutationDocument, {
    onMutate: () => {
      setSubmitting(true);
    },
    onSuccess: (response) => {
      const rSection = response?.reorderWorkflowItems?.workflowSection;
      if (!rSection) {
        setSubmitting(false);
        return;
      }

      addWorkflowSectionToCache(queryClient, rSection, workflowId || '', true);
      setSubmitted();
    },
    onError: (errors) => {
      alertGqlError(errors, 'Failed to reorder questions');

      setSubmitting(false);
    },
  });
}

export function useUpdateWorkflowItemCondition2() {
  const queryClient = useQueryClient();
  const { workflowId, setSubmitting, setSubmitted, setErrored } =
    useRequestBuilderContext();
  return useMutationGraphQL(updateWorkflowItemConditionDocument2, {
    onMutate: () => {
      setSubmitting(true);
    },
    onSettled: (conditionResponse) => {
      const workflowItemCondition =
        conditionResponse?.updateWorkflowItemCondition2?.workflowItemCondition;
      if (!workflowItemCondition) {
        setSubmitting(false);
        return;
      }

      updateWorkflowConditionInCache(
        queryClient,
        workflowItemCondition,
        workflowId,
      );
      setSubmitted();
    },
    onError: (errors, { workflowItemConditionId }) => {
      alertGqlError(errors, 'Failed to update conditional');

      setErrored(
        `${workflowItemConditionId}.match`,
        'Failed to update conditional',
      );

      setSubmitting(false);
    },
  });
}

export function useDeleteWorkflowItemCondition() {
  const { setSubmitting, setSubmitted } = useRequestBuilderContext();
  return useMutationGraphQL(deleteWorkflowItemConditionDocument, {
    onMutate: () => {
      setSubmitting(true);
    },
    onSuccess: (response) => {
      if (!response?.deleteWorkflowItemCondition) {
        setSubmitting(false);
        return;
      }

      setSubmitted();
    },
    onError: (errors) => {
      alertGqlError(errors, 'Failed to delete condition');

      setSubmitting(false);
    },
  });
}
